import CorporationSheetResponseResource from "common/resources/CorporationSheet/CorporationSheetResponseResource";
import { IPagination } from "components/elements/InfiniteScroll";
import Table from "components/elements/Table";
import { ECellType, IRowProps, TableColumn } from "components/elements/Table/MuiTable";
import I18n from "components/materials/I18n";
import ApplicationConfig from "configs/ApplicationConfig";
import { useCallback, useEffect, useRef, useState } from "react";
import CorporationService from "services/CorporationService";
import { container } from "tsyringe";

import classes from "./classes.module.scss";
import Button, { EButtonVariant } from "components/elements/Button";
import { ArrowLongRightIcon } from "@heroicons/react/24/outline";

const corporationService = container.resolve(CorporationService);
const configService = container.resolve(ApplicationConfig);

type IProps = {
	officeId: string;
	onSelectClient: (corporationSheetId: string) => void;
};

export default function Corporations({ officeId, onSelectClient }: IProps) {
	const [rows, setRows] = useState<IRowProps[]>([]);
	const [count, setCount] = useState<{ count: number }>({ count: 0 });
	const pagination = useRef<IPagination>({ skip: 0, take: configService.get().defaultPagination });
	const search = useRef<string | null>(null);

	const buildRows = useCallback(
		(corporationSheets: CorporationSheetResponseResource[]): IRowProps[] => {
			return corporationSheets.map((corporationSheet) => {
				return {
					rowKey: corporationSheet.id,
					raisonSociale: corporationSheet.raisonSociale,
					formeJuridique: corporationSheet.formeJuridique,
					montantTotalSouscriptions: corporationSheet.subscriptionSum,
					dateLastSubscription: corporationSheet.lastSubscriptionDate,
					numberOfProductSubscribed: corporationSheet.numberOfProductSubscribed,
					select: (
						<Button variant={EButtonVariant.TEXT} onClick={() => onSelectClient(corporationSheet.id)}>
							{I18n.asset.common.select}
							<ArrowLongRightIcon className={classes["icon"]} />
						</Button>
					),
				};
			});
		},
		[onSelectClient],
	);

	const fetchClients = useCallback(
		async () =>
			corporationService.getCorporationSheetsByOfficeId(officeId, pagination.current, search.current).then((clients) => {
				if (clients.length === 0) return [];

				setRows((_rows) => [..._rows, ...buildRows(clients)]);
				pagination.current.skip += pagination.current.take;
				return clients;
			}),
		[buildRows, officeId],
	);

	const onNext = useCallback(
		(release: () => void) => {
			fetchClients().then((clients) => {
				if (!clients.length) return console.warn("No more value to load");
				release();
			});
		},
		[fetchClients],
	);

	useEffect(() => {
		corporationService.countByOfficeId(officeId).then(setCount);
	}, [officeId]);

	const onSearch = useCallback((searchParam: string) => {
		pagination.current.skip = 0;
		search.current = (searchParam && searchParam.trim()) || null;
		setRows([]);
	}, []);

	return <Table columnsHead={columsHead} rows={rows} count={count.count} placeholderSearchbar={I18n.trslt(I18n.asset.common.search)} onNext={onNext} onSearch={onSearch} />;
}

const columsHead: readonly TableColumn[] = [
	{
		headContent: I18n.asset.pages.corporations.table.columns.raisonSociale,
		key: "raisonSociale",
	},
	{
		headContent: I18n.asset.pages.corporations.table.columns.formeJuridique,
		key: "formeJuridique",
	},
	{
		headContent: I18n.asset.pages.clients.table.columns.number_of_subscriptions,
		key: "numberOfProductSubscribed",
	},
	{
		headContent: I18n.asset.pages.corporations.table.columns.montantTotalSouscriptions,
		key: "montantTotalSouscriptions",
		formatType: ECellType.EURO,
	},
	{
		headContent: I18n.asset.pages.corporations.table.columns.dateLastSubscription,
		key: "dateLastSubscription",
		formatType: ECellType.DATE,
	},
	{
		headContent: "",
		key: "select",
	},
];
